/**
 * BasicInfoSection - Event basic info form fields
 * Extracted from EventsTab.jsx
 */
import { useRef, useState } from "react";
import { Wand2, Upload, Image } from "lucide-react";
import { extractDominantColor } from "../../../utils/colorExtractor";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const BasicInfoSection = ({ formData, setFormData, SPORTS }) => (
  <>
    {/* Basic Info */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs text-zinc-500 uppercase mb-1">Event Name *</label>
        <input 
          type="text" 
          value={formData.name} 
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          placeholder="e.g., Holiday Classic Tournament"
        />
      </div>
      <div>
        <label className="block text-xs text-zinc-500 uppercase mb-1">Sport *</label>
        <select 
          value={formData.sport} 
          onChange={(e) => setFormData({...formData, sport: e.target.value})}
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        >
          {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </div>

    <div>
      <label className="block text-xs text-zinc-500 uppercase mb-1">Description</label>
      <textarea 
        value={formData.description} 
        onChange={(e) => setFormData({...formData, description: e.target.value})}
        rows={3}
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        placeholder="Event description..."
      />
    </div>
  </>
);

export const DateLocationSection = ({ formData, setFormData }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
    <div>
      <label className="block text-xs text-zinc-500 uppercase mb-1">Start Date *</label>
      <input 
        type="date" 
        value={formData.start_date} 
        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
        required
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
      />
    </div>
    <div>
      <label className="block text-xs text-zinc-500 uppercase mb-1">End Date</label>
      <input 
        type="date" 
        value={formData.end_date} 
        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
      />
    </div>
    <div>
      <label className="block text-xs text-zinc-500 uppercase mb-1">Location</label>
      <input 
        type="text" 
        value={formData.location} 
        onChange={(e) => setFormData({...formData, location: e.target.value})}
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        placeholder="City, State"
      />
    </div>
    <div>
      <label className="block text-xs text-zinc-500 uppercase mb-1">Venue</label>
      <input 
        type="text" 
        value={formData.venue} 
        onChange={(e) => setFormData({...formData, venue: e.target.value})}
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        placeholder="Venue name"
      />
    </div>
  </div>
);

export const BrandingSection = ({ formData, setFormData }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleExtractColor = async () => {
    try {
      const color = await extractDominantColor(formData.logo_url);
      if (color) setFormData({...formData, color});
    } catch (e) { 
      console.error(e); 
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      const response = await fetch(`${API}/uploads/image`, {
        method: "POST",
        body: uploadFormData,
      });

      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, logo_url: data.url });
      } else {
        console.error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="block text-xs text-zinc-500 uppercase mb-1">Event Logo</label>
        <div className="flex gap-2 mb-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleLogoUpload}
            accept="image/*"
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold disabled:opacity-50"
          >
            <Upload className="w-3 h-3" />
            {uploading ? "Uploading..." : "Upload"}
          </button>
          <span className="text-zinc-500 text-xs self-center">or paste URL below</span>
        </div>
        <input 
          type="text" 
          value={formData.logo_url} 
          onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          placeholder="https://..."
        />
        {formData.logo_url && (
          <div className="mt-2 p-3 bg-zinc-900/50 border border-zinc-700 rounded flex items-center gap-3">
            <img 
              src={formData.logo_url} 
              alt="Logo preview" 
              className="w-14 h-14 object-contain bg-white/5 p-1 rounded"
              onError={(e) => e.target.style.display = 'none'}
            />
            <button
              type="button"
              onClick={handleExtractColor}
              className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold rounded"
            >
              <Wand2 className="w-3 h-3" /> Extract Color
            </button>
          </div>
        )}
      </div>
      <div>
        <label className="block text-xs text-zinc-500 uppercase mb-1">Event Color</label>
        <div className="flex gap-2">
          <input 
            type="color" 
            value={formData.color} 
            onChange={(e) => setFormData({...formData, color: e.target.value})}
            className="w-12 h-10 bg-zinc-800 border border-white/10"
          />
          <input 
            type="text" 
            value={formData.color} 
            onChange={(e) => setFormData({...formData, color: e.target.value})}
            className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div className="flex items-end">
        <label className="flex items-center gap-2 cursor-pointer">
          <input 
            type="checkbox" 
            checked={formData.is_published}
            onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
            className="w-4 h-4"
          />
          <span className="text-sm text-zinc-400">Publish Event</span>
        </label>
      </div>
    </div>
  );
};

export default BasicInfoSection;
